import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BlocNature,
  ChampRegle,
  CiblePivot,
  ColonnePivot,
} from '../../../../lib/correspondance.js';
import { champAlimente } from '../../../../lib/reglesEdition.js';
import reprise from '../Reprise.module.css';
import { libelleRef } from './libelles.js';
import styles from './Correspondance.module.css';

/** Ce qu'un champ lit, en une ligne : « Prénom + Nom », « constante », ou « non alimenté ». */
function resume(t: ReturnType<typeof useTranslation>['t'], champ: ChampRegle | undefined): string {
  if (!champAlimente(champ) || !champ) return String(t('correspondance.champs.nonAlimente'));
  const sources = champ.sources.filter((reference) => reference.champ.trim() !== '');
  if (sources.length === 0) return String(t('correspondance.champs.constante'));
  return sources.map((reference) => libelleRef(t, reference)).join(' + ');
}

/**
 * Le volet de gauche : les champs cibles de la nature, tels que les gabarits
 * les définissent (`GET …/profils/cibles`). Les obligatoires passent d'abord
 * et portent leur marque ; chaque ligne dit d'où le champ est alimenté, et
 * s'il porte une erreur.
 */
export function ChampsCibles({
  cible,
  bloc,
  selection,
  onSelectionner,
  erreursParChamp,
  refusParColonne,
}: {
  cible: CiblePivot;
  bloc: BlocNature;
  selection: string | null;
  onSelectionner: (cle: string) => void;
  /** Nombre d'erreurs de règles par clé de champ. */
  erreursParChamp: ReadonlyMap<string, number>;
  /** Lignes refusées par l'aperçu, par en-tête de colonne du pivot. */
  refusParColonne: ReadonlyMap<string, number>;
}): JSX.Element {
  const { t } = useTranslation('console');
  const groupes: { cle: 'obligatoires' | 'autres'; colonnes: ColonnePivot[] }[] = [
    { cle: 'obligatoires', colonnes: cible.colonnes.filter((colonne) => colonne.obligatoire) },
    { cle: 'autres', colonnes: cible.colonnes.filter((colonne) => !colonne.obligatoire) },
  ];
  const alimentes = cible.colonnes.filter((colonne) =>
    champAlimente(bloc.colonnes[colonne.cle]),
  ).length;

  return (
    <nav className={styles.listeChamps} aria-label={t('correspondance.champs.aria')}>
      <p className={reprise.discret} aria-live="polite">
        {t('correspondance.champs.bilan', { alimentes, total: cible.colonnes.length })}
      </p>
      {groupes
        .filter((groupe) => groupe.colonnes.length > 0)
        .map((groupe) => (
          <div key={groupe.cle} className={styles.groupeChamps}>
            <h4 className={styles.groupeTitre}>{t(`correspondance.champs.${groupe.cle}`)}</h4>
            <ul className={styles.champsListe}>
              {groupe.colonnes.map((colonne) => {
                const champ = bloc.colonnes[colonne.cle];
                const alimente = champAlimente(champ);
                const erreurs = erreursParChamp.get(colonne.cle) ?? 0;
                const refus = refusParColonne.get(colonne.entete) ?? 0;
                const manque = colonne.obligatoire && !alimente;
                return (
                  <li key={colonne.cle}>
                    <button
                      type="button"
                      className={styles.champLigne}
                      aria-pressed={selection === colonne.cle}
                      data-etat={erreurs > 0 || manque ? 'erreur' : alimente ? 'alimente' : 'vide'}
                      onClick={() => onSelectionner(colonne.cle)}
                    >
                      <span className={styles.champNom}>
                        {colonne.entete}
                        {colonne.obligatoire ? (
                          <span
                            className={styles.marque}
                            aria-label={t('correspondance.champs.obligatoireMarque')}
                          >
                            *
                          </span>
                        ) : null}
                      </span>
                      <span className={styles.champSource}>{resume(t, champ)}</span>
                      {erreurs > 0 ? (
                        <span className={styles.champAlerte}>
                          {t('correspondance.champs.erreurs', { count: erreurs })}
                        </span>
                      ) : refus > 0 ? (
                        <span className={styles.champAlerte}>
                          {t('correspondance.champs.refus', { count: refus })}
                        </span>
                      ) : manque ? (
                        <span className={styles.champAlerte}>
                          {t('correspondance.champs.requis')}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
    </nav>
  );
}
